import React, { useEffect, useState } from "react";
import get from "lodash/get";

import { USHER } from "../../constants";
import { getUser } from "../../state/User";
import { haveDestinations } from "../destinations/EnsureDestinations";
import {
  formatDateAsString,
  formatDestinationAsString,
  monthIdxToString,
} from "../helpers";
import Center from "../utils/Center";
import Loading from "../utils/Loading";
import JobPreview from "./JobPreview";
import JobSnapshotsGraph from "./JobSnapshotsGraph";

type TableRow = {
  key: string;
  jobs: CampaignStatsItem[];
};

export default function CampaignReport({ post }: { post: Post }) {
  const [error, setError] = useState<string>();
  const [destinationFilterId, setDestinationFilterId] = useState<string | null>(
    null
  );
  const [data, setData] = useState<CampaignStatsItem[] | null>(null);
  const [typeOfGraph, setTypeOfGraph] = useState<"date" | "dayOfWeek" | "time">(
    "date"
  );
  const user = getUser();

  useEffect(() => {
    async function getData() {
      try {
        const res = await fetch(USHER, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            campaign_stats: true,
            campaignId: post.id,
          }),
        });
        if (res.ok) {
          let data: CampaignStatsItem[] = await res.json();
          data = data.map((d) => ({ ...d, postedAt: new Date(d.postedAt) }));
          // @ts-ignore
          data.sort((a, b) => a.postedAt - b.postedAt);
          setData(data);
        } else {
          setError("Can not get the stats for this campaign.");
        }
      } catch (err) {
        console.log(err);
        setError("Can not get the stats for this campaign.");
      }
    }
    getData();
  }, []);

  function getGraphData() {
    if (!data) return [];
    if (destinationFilterId === null) return [...data];
    return [...data].filter(
      (item) => item.destinationId === destinationFilterId
    );
  }

  if (data !== null) {
    const totalEngagements = data.reduce(
      (res, item) => res + (item.stats?.engagements || 0),
      0
    );
    if (data.length === 0) {
      return <small className="tac mb2 mt2 block">No data yet.</small>;
    }
    return (
      <>
        <h2 className="tac mb1">
          {totalEngagements} user engagement{totalEngagements !== 1 ? "s" : ""}{" "}
          in total
        </h2>
        <JobSnapshotsGraph stats={post.stats} />
        {haveDestinations(user) && (
          <Center className="select mt1 mb1" width={300}>
            <select
              onChange={(e) =>
                setDestinationFilterId(
                  e.target.value === "all" ? null : e.target.value
                )
              }
            >
              <option value="all">Across all destinations</option>
              {user?.destinations.map((d) => {
                return (
                  <option value={d.id} key={d.id}>
                    {formatDestinationAsString(d)}
                  </option>
                );
              })}
            </select>
          </Center>
        )}
        {
          <p className="tac mb2">
            {typeOfGraph === "date" ? (
              "By date"
            ) : (
              <button
                className="reset as-link"
                onClick={() => setTypeOfGraph("date")}
              >
                By date
              </button>
            )}
            {" | "}
            {typeOfGraph === "dayOfWeek" ? (
              "Day of the week"
            ) : (
              <button
                className="reset as-link"
                onClick={() => setTypeOfGraph("dayOfWeek")}
              >
                Day of the week
              </button>
            )}
            {" | "}
            {typeOfGraph === "time" ? (
              "Time of the day"
            ) : (
              <button
                className="reset as-link"
                onClick={() => setTypeOfGraph("time")}
              >
                Time of the day
              </button>
            )}
          </p>
        }
        {typeOfGraph === "date" && (
          <Graph
            post={post}
            user={user as User}
            data={getGraphData().map((item) => {
              return {
                x: formatDateAsString(item.postedAt, true),
                y: item.stats.engagements,
                job: item,
              };
            })}
            xAxisLabel="Date"
            typeOfGraph="date"
          />
        )}
        {typeOfGraph === "dayOfWeek" && (
          <Graph
            post={post}
            user={user as User}
            data={getGraphData()
              .sort((a, b) => a.postedAt.getDay() - b.postedAt.getDay())
              .map((item) => {
                return {
                  x: getDayOfTheWeek(item.postedAt),
                  y: item.stats.engagements,
                  job: item,
                };
              })}
            xAxisLabel="Day of the week"
          />
        )}
        {typeOfGraph === "time" && (
          <Graph
            post={post}
            user={user as User}
            data={getGraphData()
              .sort(
                (a, b) => getInMinutes(a.postedAt) - getInMinutes(b.postedAt)
              )
              .map((item) => {
                return {
                  x: getHour(item.postedAt),
                  y: item.stats.engagements,
                  job: item,
                };
              })}
            xAxisLabel="Time"
          />
        )}
      </>
    );
  }

  if (error) {
    return (
      <Center width={200}>
        <p className="tac error mt1">{error}</p>
      </Center>
    );
  }

  return (
    <>
      <Center width={40}>
        <Loading />
      </Center>
      <Center width={200}>
        <p className="tac mt1">Getting statistical data. Please wait.</p>
      </Center>
    </>
  );
}

function Graph({
  data,
  xAxisLabel,
  post,
  user,
  typeOfGraph,
}: {
  data: { x: any; y: any; job: CampaignStatsItem }[];
  xAxisLabel: string;
  post: Post;
  user: User;
  typeOfGraph?: string;
}) {
  if (data.length === 0) {
    /* ******************************************************************** No data */
    return <small className="tac mb2 mt2 block">No data.</small>;
  } else {
    const rows: TableRow[] = data.reduce((res, { x, job }) => {
      const exists = res.find((r) => r.key === x);
      if (exists) {
        exists.jobs.push(job);
      } else {
        res.push({ key: x, jobs: [job] });
      }
      return res;
    }, []);
    const rowsByMonth: {
      month: number;
      year: number;
      total: number;
    }[] =
      typeOfGraph === "date"
        ? data.reduce((res, { x, job }) => {
            const date = new Date(x);
            const exists = res.find(
              (r) =>
                r.month === date.getMonth() && r.year === date.getFullYear()
            );
            if (exists) {
              exists.total += get(job, "stats.engagements", 0);
            } else {
              res.push({
                total: get(job, "stats.engagements", 0),
                month: date.getMonth(),
                year: date.getFullYear(),
              });
            }
            return res;
          }, [])
        : [];

    return (
      <table className="mb2">
        <thead>
          <tr>
            <th>{xAxisLabel}</th>
            <th>Engagements</th>
            <th>Posts</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            let monthRow;
            const date = new Date(row.key);
            const monthFoundIdx = rowsByMonth.findIndex(
              (m) =>
                m.month === date.getMonth() && m.year === date.getFullYear()
            );
            if (monthFoundIdx >= 0) {
              const monthFound = rowsByMonth.splice(monthFoundIdx, 1)[0];
              monthRow = (
                <tr
                  style={{
                    background: "var(--light-bg-color)",
                  }}
                >
                  <td className="tac" colSpan={3}>
                    {monthFound
                      ? `${monthIdxToString(monthFound.month)} ${
                          monthFound.year
                        } (${monthFound.total})`
                      : ""}
                  </td>
                </tr>
              );
            }
            return (
              <React.Fragment key={i}>
                {monthRow ? monthRow : null}
                <tr>
                  <td>{row.key}</td>
                  <td>
                    {row.jobs.reduce(
                      (total, j) => total + j?.stats?.engagements || 0,
                      0
                    )}
                  </td>
                  <td>
                    <Posts jobs={row.jobs} post={post} user={user} />
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  }
}

function Posts({
  jobs,
  post,
  user,
}: {
  jobs: CampaignStatsItem[];
  post: Post;
  user: User;
}) {
  const [expanded, expand] = useState(false);
  if (expanded) {
    return (
      <>
        {jobs.map((job, j) => (
          <JobPreview
            key={j}
            job={{ id: job.jobId } as Job}
            post={post}
            user={user}
            asLi={false}
          />
        ))}
      </>
    );
  }
  return (
    <button className="reset as-link" onClick={() => expand(true)}>
      {jobs.length} post{jobs.length === 1 ? "" : "s"}
    </button>
  );
}
function getDayOfTheWeek(d: Date) {
  return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()];
}
function getHour(d: Date) {
  let hour = d.getHours();
  if (hour < 10) {
    hour = "0" + hour;
  }
  return `${hour}:00`;
}
function getInMinutes(d: Date) {
  return d.getHours() * 60 + d.getMinutes();
}
